import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { H1, Sub, IconBtn } from "./_shared";

export default function SportsSection() {
  const [sports, setSports] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/sports").then((r) => setSports(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const del = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try { await api.delete(`/admin/sports/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  return (
    <div data-testid="cp-sports">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><H1>Sports</H1><Sub>Manage the sports catalog.</Sub></div>
        <button onClick={() => setCreating(true)} data-testid="cp-sport-new" className="pp-btn-primary px-4 py-2 rounded-lg flex items-center gap-2"><Plus size={14} /> New sport</button>
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {sports.map((s) => (
          <div key={s.id} className="pp-card p-4 flex items-center justify-between" data-testid={`cp-sport-${s.slug}`}>
            <div>
              <div className="font-bold">{s.name}</div>
              <div className="text-xs text-zinc-500">/{s.slug} · {s.is_active ? "active" : "inactive"}</div>
            </div>
            <div className="flex gap-1">
              <IconBtn onClick={() => setEditing(s)}><Pencil size={14} /></IconBtn>
              <IconBtn onClick={() => del(s.id, s.name)} danger><Trash2 size={14} /></IconBtn>
            </div>
          </div>
        ))}
      </div>
      {(creating || editing) && (
        <SportDialog
          sport={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SportDialog({ sport, onClose, onSaved }) {
  const [f, setF] = useState({ name: sport?.name || "", slug: sport?.slug || "", is_active: sport?.is_active ?? true, icon_url: sport?.icon_url || "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (sport) await api.patch(`/admin/sports/${sport.id}`, f);
      else await api.post("/admin/sports", f);
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#131313] border-white/10 text-white max-w-md" data-testid="cp-sport-dialog">
        <DialogHeader><DialogTitle>{sport ? `Edit ${sport.name}` : "New sport"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input className="pp-input" required placeholder="Name (e.g. Pickleball)" value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} data-testid="cp-sport-name" />
          <input className="pp-input" required placeholder="slug (e.g. pickleball)" value={f.slug} onChange={(e) => setF((s) => ({ ...s, slug: e.target.value }))} data-testid="cp-sport-slug" />
          <input className="pp-input" placeholder="Icon URL (optional)" value={f.icon_url} onChange={(e) => setF((s) => ({ ...s, icon_url: e.target.value }))} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_active} onChange={(e) => setF((s) => ({ ...s, is_active: e.target.checked }))} /> Active</label>
          <DialogFooter>
            <button type="button" onClick={onClose} className="pp-btn-secondary px-4 py-2 rounded-lg">Cancel</button>
            <button data-testid="cp-sport-submit" className="pp-btn-primary px-4 py-2 rounded-lg">Save</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
